import {
  PerformanceEventRecorder,
  PerformanceEventImpl,
  PerformanceEventRecorderOptions
} from "./performance-event-recorder";
import { LoggingService } from "../loggingSvc";
import { TelemetryServiceImplementation, CustomKeyValues } from "../../../ShellCommon/logger/telemetry-service";
import { GlobalTelemetryConstants } from "../../../ShellCommon/logger/telemetry-constants";
import { v4 } from "uuid";
import { FlightingService } from "../flightingSvc";
import { ScenarioEventRecorderOptions, ScenarioEventRecorder } from "./performance-event-recorder";
import * as Jsll from "../../../ShellCommon/logger/jsll-types";

const SD_EXCLUDE_INACTIVITY_FROM_PERFLOG = "SD_EXCLUDE_INACTIVITY_FROM_PERFLOG";
const CASE_LOAD = "case_load";
const CASE_CREATE = "case_create";

/** Performance measure service  */
export interface PerformanceMeasureService {
  /** Gets or creates a performance event recorder that can be used to report performance information.
   * @param name The name will be use to report the event to our performance pipeline, the name should encompass the scenario that is being recorded.
   * @throws If the event has already been registered with different options parameters
   */
  getOrCreateEventRecorder(
    name: string,
    options: PerformanceEventRecorderOptions,
    serviceName?: string,
    identifier?: string | number
  ): PerformanceEventRecorder;
}

/** Scenario measure service  */
export interface ScenarioMeasureService extends PerformanceMeasureService {}

const performanceQosEventsVersion = "v1.0";
const performanceLoggerWindowIdStorageKey = "SDPerformanceLoggerWindowId";

export interface ScenarioQosEvent {
  event: { name: string; options: PerformanceEventRecorderOptions; timesRecorded: number };
  data?: CustomKeyValues;
}

export interface ScenarioQosStartedEvent extends ScenarioQosEvent {
  scenarioQosStartedEventVersion: string;
}

export interface ScenarioQosCheckpointEvent extends ScenarioQosEvent {
  performanceQosCheckpointEventVersion: string;
  durationMs?: number;
  wallClockDurationMs?: number;
}

export interface ScenarioQosCompletedEvent extends ScenarioQosEvent {
  scenarioQosCompletedEventVersion: string;
  success?: boolean;
  incomplete?: boolean;
  error?: string;
  durationMs?: number;
  wallClockDurationMs?: number;
  invalidate?: boolean;
  inactivityTime?: number;
}

interface StartTimeEntry {
  startTime: number;
  sessionIdentifer: string;
}

interface InitialDurationEntry {
  duration: number;
  sessionIdentifer: string;
}

export class PerformanceMeasureServiceImpl implements PerformanceMeasureService {
  private performance: Performance;
  private sessionIdentifer: string;
  private registeredEvents: Map<string, PerformanceEventImpl> = new Map<string, PerformanceEventImpl>();
  private crossSessionEvents: PerformanceEventImpl[] = [];

  // @ngInject
  constructor(
    private loggingService: LoggingService,
    private telemetryService: TelemetryServiceImplementation,
    private $window: ng.IWindowService,
    private flightingService: FlightingService,
    private telemetryConstants: GlobalTelemetryConstants
  ) {
    this.performance = $window.performance;
    if (!this.$window.sessionStorage.getItem(performanceLoggerWindowIdStorageKey)) {
      this.sessionIdentifer = v4();
      this.$window.sessionStorage.setItem(performanceLoggerWindowIdStorageKey, this.sessionIdentifer);
    } else {
      this.sessionIdentifer = this.$window.sessionStorage.getItem(performanceLoggerWindowIdStorageKey);
    }

    this.$window.addEventListener("beforeunload", this.recordNavigateAway.bind(this));
  }

  public getOrCreateEventRecorder(
    name: string,
    options: ScenarioEventRecorderOptions,
    serviceName?: string,
    identifier?: string | number
  ): ScenarioEventRecorder {
    // give option to have same event name, but different identifier like different caseId, visitId
    let eventIdentifier = identifier ? identifier + name : name;
    let alreadyRegisteredEvent = this.registeredEvents.get(eventIdentifier);
    if (alreadyRegisteredEvent) {
      // temporary check to avoid problems when the description is of type object and it can be different for the same type of event/scenario.
      // only Email app is using the description property as object today as we need to pass extra info which can change for the same event.
      // If anyone also wants to use, please pay attention here.
      if (this.isMatch(options, alreadyRegisteredEvent.options)) {
        throw new Error(`Unable to get ${eventIdentifier} as its options do not match the already registered options.`);
      }
      return alreadyRegisteredEvent;
    } else {
      let event = new PerformanceEventImpl(this, name, options, serviceName, identifier);
      this.registeredEvents.set(eventIdentifier, event);
      if (event.options.isCrossWindowOrTab) {
        this.crossSessionEvents.push(event);
      }
      return event;
    }
  }

  public recordStartedEvent(
    event: PerformanceEventImpl,
    data?: CustomKeyValues,
    userActionTriggered?: boolean
  ): boolean {
    let scenarioQosStartedEvent = this.createStartedEvent(event, data);
    if (!event.options.isCrossWindowOrTab) {
      if (this.getStartTimeForPerformanceEvent(event)) {
        this.performance.clearMarks(event.startedName);
      }
      this.performance.mark(event.startedName);
      if (event.options.alwaysLog) {
        this.pushTelemetryStartEvent(event, scenarioQosStartedEvent, userActionTriggered);
      }
    } else {
      this.pushTelemetryStartEvent(event, scenarioQosStartedEvent, userActionTriggered);
      let startTime =
        event.options.shouldIncludeBootstrapTimeOnStart && this.$window.SDBootstrapStart
          ? this.$window.SDBootstrapStart
          : new Date();
      let startEntry: StartTimeEntry = { startTime: startTime.getTime(), sessionIdentifer: this.sessionIdentifer };
      this.$window.localStorage.setItem(event.startKey, JSON.stringify(startEntry));
    }
    return true;
  }

  public recordCheckpointEvent(
    event: PerformanceEventImpl,
    userActionTriggered: boolean,
    data?: CustomKeyValues
  ): boolean {
    let performanceQosCheckpointEvent = this.createCheckpointEvent(event, data);
    return this.pushTelemetryCheckpointEvent(event, performanceQosCheckpointEvent, userActionTriggered);
  }

  public recordSuccessEvent(
    event: PerformanceEventImpl,
    data?: CustomKeyValues,
    userActionTriggered?: boolean
  ): boolean {
    return this.recordCompletedEvent(event, undefined, data, userActionTriggered);
  }

  public recordFailureEvent(
    event: PerformanceEventImpl,
    error: string,
    data?: CustomKeyValues,
    userActionTriggered?: boolean
  ): boolean {
    return this.recordCompletedEvent(event, error, data, userActionTriggered);
  }

  public recordCancelEvent(event: PerformanceEventImpl, userActionTriggered: boolean, data?: CustomKeyValues): boolean {
    return this.recordCompletedEvent(event, undefined, data, userActionTriggered, true);
  }

  public recordCompletedEvent(
    event: PerformanceEventImpl,
    error: string,
    data?: CustomKeyValues,
    userActionTriggered?: boolean,
    isCancelEvent?: boolean
  ) {
    let scenarioQosCompletedEvent = this.createCompletedEvent(event, error, data);
    if (event.options.isCrossWindowOrTab) {
      scenarioQosCompletedEvent = this.getPerfQosForCrossSessionEvent(event, scenarioQosCompletedEvent);
    } else {
      scenarioQosCompletedEvent = this.getPerfQosForPerformanceEvent(event, scenarioQosCompletedEvent);
    }

    // If the event was unable to be completed (success or failure), do not log any telemetry for the scenario
    if (scenarioQosCompletedEvent === undefined) {
      return false;
    }

    scenarioQosCompletedEvent.success = scenarioQosCompletedEvent.error === undefined;
    if (
      scenarioQosCompletedEvent.invalidate &&
      this.flightingService.isFlightEnabled(SD_EXCLUDE_INACTIVITY_FROM_PERFLOG) &&
      (event.name.toLowerCase() === CASE_LOAD || event.name.toLowerCase() === CASE_CREATE)
    ) {
      this.loggingService.logInfo(scenarioQosCompletedEvent);
    } else {
      this.pushTelemetryCompleteEvent(event, scenarioQosCompletedEvent, userActionTriggered, isCancelEvent);
    }
    return scenarioQosCompletedEvent.incomplete !== false;
  }

  private pushTelemetryStartEvent(
    event: PerformanceEventImpl,
    scenarioQosStartedEvent?: ScenarioQosStartedEvent,
    userActionTriggered?: boolean
  ) {
    this.loggingService.logInfo(scenarioQosStartedEvent);
    let pageMetaTags = { scn: event.name };
    let content = {
      name: event.name,
      timesRecorded: event.timesRecorded.toString(),
      performanceQosEventsVersion: performanceQosEventsVersion
    };
    let description = this.getDescription(event);
    let pageTags = angular.merge({}, scenarioQosStartedEvent.data || event.options.scenarioData || description);
    pageTags.metaTags = pageMetaTags;
    if (userActionTriggered) {
      this.telemetryService.pushPageActionEvent(
        this.telemetryConstants.pageActionType.click,
        this.telemetryConstants.pageActionBehavior.StartProcess,
        undefined,
        content,
        pageTags,
        event.serviceName
      );
    } else {
      this.telemetryService.pushContentUpdateEvent({
        behavior: this.telemetryConstants.pageActionBehavior.StartProcess,
        serviceName: event.serviceName,
        content: content,
        pageTags: pageTags
      });
    }
  }

  private pushTelemetryCheckpointEvent(
    event: PerformanceEventImpl,
    performanceQosCheckpointEvent: ScenarioQosCheckpointEvent,
    userActionTriggered?: boolean
  ): boolean {
    let startTime: number;
    if (event.options.isCrossWindowOrTab) {
      startTime = this.getStartedTimeForCrossSessionEvent(event, false);
    } else {
      let marker = this.getStartTimeForPerformanceEvent(event);
      if (marker) {
        startTime = marker.duration;
      }
    }
    if (!startTime) {
      return false;
    }
    let currentTimeMs = new Date().getTime();
    let pageMetaTags = <CustomKeyValues>{
      scn: event.name,
      durationMs: (currentTimeMs - startTime).toString()
    };
    let content = {
      name: event.name,
      timesRecorded: event.timesRecorded.toString(),
      performanceQosEventsVersion: performanceQosEventsVersion,
      isCrossWindow: event.options.isCrossWindowOrTab ? "true" : "false"
    };
    let pageTags = <Jsll.PageTags>{
      metaTags: pageMetaTags
    };
    let description = this.getDescription(event);
    pageTags = angular.merge(
      {},
      pageTags,
      performanceQosCheckpointEvent.data || event.options.scenarioData || description
    );
    if (userActionTriggered) {
      this.telemetryService.pushPageActionEvent(
        this.telemetryConstants.pageActionType.click,
        this.telemetryConstants.pageActionBehavior.ProcessCheckpoint,
        undefined,
        content,
        pageTags,
        event.serviceName
      );
    } else {
      this.telemetryService.pushContentUpdateEvent({
        behavior: this.telemetryConstants.pageActionBehavior.ProcessCheckpoint,
        serviceName: event.serviceName,
        pageTags: performanceQosCheckpointEvent.data || event.options.scenarioData,
        content: content
      });
    }
  }

  private pushTelemetryCompleteEvent(
    event: PerformanceEventImpl,
    scenarioQosCompletedEvent: ScenarioQosCompletedEvent,
    userActionTriggered?: boolean,
    isCancelEvent?: boolean
  ) {
    this.loggingService.logInfo(scenarioQosCompletedEvent);
    let durationMs = scenarioQosCompletedEvent.durationMs.toString();
    let pageMetaTags = <CustomKeyValues>{
      scn: event.name,
      durationMs: durationMs
    };
    let actionBehavior = this.telemetryConstants.pageActionBehavior.CompleteProcess;
    let content = <CustomKeyValues>{
      name: event.name,
      durationMs: durationMs,
      timesRecorded: event.timesRecorded.toString(),
      isCrossWindow: event.options.isCrossWindowOrTab ? "true" : "false",
      performanceQosEventsVersion: performanceQosEventsVersion,
      identifier: event.identifier
    };
    if (isCancelEvent) {
      actionBehavior = this.telemetryConstants.pageActionBehavior.CancelProcess;
    } else {
      // we need the isSuccess flag and error property in the Complete process only.
      pageMetaTags.isSuccess = scenarioQosCompletedEvent.success ? "true" : "false";
      pageMetaTags.scnerror = scenarioQosCompletedEvent.error;
      content.error = scenarioQosCompletedEvent.error;
    }
    let pageTags = <Jsll.PageTags>{
      metaTags: pageMetaTags,
      timings: { durationMs: durationMs }
    };
    let description = this.getDescription(event);
    pageTags = angular.merge({}, pageTags, scenarioQosCompletedEvent.data || event.options.scenarioData || description);
    if (userActionTriggered) {
      this.telemetryService.pushPageActionEvent(
        this.telemetryConstants.pageActionType.click,
        actionBehavior,
        undefined,
        content,
        pageTags,
        event.serviceName
      );
    } else {
      this.telemetryService.pushContentUpdateEvent({
        behavior: actionBehavior,
        serviceName: event.serviceName,
        pageTags: pageTags,
        content: content
      });
    }
  }

  private getDescription(event: PerformanceEventImpl) {
    let description = event.options.description;
    if (event.options.description && typeof event.options.description !== "object") {
      description = { contentDescription: description };
    }
    return description;
  }

  private isMatch(
    currentEventOptions: PerformanceEventRecorderOptions,
    existingEventOptions: PerformanceEventRecorderOptions
  ): boolean {
    let isDescriptionAnObject = currentEventOptions.description && typeof currentEventOptions.description === "object";
    return !isDescriptionAnObject && !angular.equals(existingEventOptions, currentEventOptions);
  }

  private getStartedTimeForCrossSessionEvent(event: PerformanceEventImpl, eraseAfterRead: boolean): number {
    let startTime: number;
    let startTimeEntryStr = this.$window.localStorage.getItem(event.startKey);
    if (startTimeEntryStr) {
      try {
        let startTimeEntry: StartTimeEntry = JSON.parse(startTimeEntryStr);
        if (this.sessionIdentifer !== startTimeEntry.sessionIdentifer) {
          // If this event should not be reported from this session ignore it
          return undefined;
        }
        startTime = startTimeEntry.startTime;
      } catch (e) {
        this.loggingService.logWarning(`Unable to parse ${event.startKey} from ${startTimeEntryStr}`);
        this.$window.localStorage.removeItem(event.startKey);
      }
    }

    if (eraseAfterRead) {
      this.$window.localStorage.removeItem(event.startKey);
    }

    return startTime;
  }

  private getPerfQosForCrossSessionEvent(
    event: PerformanceEventImpl,
    scenarioQosCompletedEvent: ScenarioQosCompletedEvent
  ): ScenarioQosCompletedEvent {
    let currentTimeMs = new Date().getTime();
    let startTime = this.getStartedTimeForCrossSessionEvent(event, true);
    if (!startTime) {
      return undefined;
    }

    try {
      if (!this.$window.SDBootstrapStart) {
        return this.completePerfQos(scenarioQosCompletedEvent, "missing bootstrap ms", false);
      }

      scenarioQosCompletedEvent.wallClockDurationMs = currentTimeMs - startTime;

      if (event.options.isRecordPageLoadTime) {
        if (
          this.flightingService.isFlightEnabled(SD_EXCLUDE_INACTIVITY_FROM_PERFLOG) &&
          (event.name.toLowerCase() === CASE_LOAD || event.name.toLowerCase() === CASE_CREATE)
        ) {
          // if window.inactivityTime is not zero it means the user navigated to a different tab, thus making the
          // tab inactive and suspending execution.
          // for user perceived performance to be accurate we want to account for this inactivity and deduct it
          // from the total scenario latency
          // Also if the tab is hidden when we finish the event, we invalidate it and not log telemetry
          // we are restricting this change to only CASE_LOAD and CASE_CREATE events for now

          if (this.$window.inactivityTime > 0) {
            scenarioQosCompletedEvent.durationMs =
              scenarioQosCompletedEvent.wallClockDurationMs - this.$window.inactivityTime;
            scenarioQosCompletedEvent.inactivityTime = this.$window.inactivityTime;
          } else if (document.visibilityState === "hidden") {
            scenarioQosCompletedEvent.invalidate = true;
          } else {
            if (
              this.$window.performance &&
              this.$window.performance.timing &&
              this.$window.performance.timing.navigationStart
            ) {
              scenarioQosCompletedEvent.durationMs = currentTimeMs - this.$window.performance.timing.navigationStart;
            } else {
              scenarioQosCompletedEvent.durationMs = currentTimeMs - this.$window.SDBootstrapStart.getTime();
            }
          }
        } else {
          if (
            this.$window.performance &&
            this.$window.performance.timing &&
            this.$window.performance.timing.navigationStart
          ) {
            scenarioQosCompletedEvent.durationMs = currentTimeMs - this.$window.performance.timing.navigationStart;
          } else {
            scenarioQosCompletedEvent.durationMs = currentTimeMs - this.$window.SDBootstrapStart.getTime();
          }
        }
      } else {
        scenarioQosCompletedEvent.durationMs = scenarioQosCompletedEvent.wallClockDurationMs;
      }

      if (event.options.shouldReportNonWallClockTime) {
        let initialDurationEntryString = this.$window.localStorage.getItem(event.startDurationLocalStorageKey);
        if (!initialDurationEntryString) {
          return this.completePerfQos(scenarioQosCompletedEvent, "missing last session duration value", false);
        }
        let initialDurationEntry: InitialDurationEntry;
        try {
          initialDurationEntry = JSON.parse(initialDurationEntryString);
          if (this.sessionIdentifer !== initialDurationEntry.sessionIdentifer) {
            return this.completePerfQos(
              scenarioQosCompletedEvent,
              "session identifier did not match, ignoring this event",
              true
            );
          }
        } catch (e) {
          this.loggingService.logWarning(
            `Performance logger: Unable to parse ${event.startDurationLocalStorageKey} from ${initialDurationEntryString}`
          );
          return this.completePerfQos(scenarioQosCompletedEvent, "Unable to parse InitialDurationEntry", false);
        }
        let currentWindowTimeMs = currentTimeMs - this.$window.SDBootstrapStart.getTime();
        scenarioQosCompletedEvent.durationMs = currentWindowTimeMs + initialDurationEntry.duration;
      }
    } finally {
      this.$window.localStorage.removeItem(event.startKey);
      this.$window.localStorage.removeItem(event.startDurationLocalStorageKey);
    }

    return scenarioQosCompletedEvent;
  }

  private getStartTimeForPerformanceEvent(event: PerformanceEventImpl): PerformanceEntry {
    let startedEventType = "mark";
    let measurements: PerformanceEntry[] = this.performance.getEntriesByName(event.startedName, startedEventType);
    if (measurements.length > 0) {
      return measurements[0];
    }
  }

  private getPerfQosForPerformanceEvent(
    event: PerformanceEventImpl,
    scenarioQosCompletedEvent: ScenarioQosCompletedEvent
  ): ScenarioQosCompletedEvent {
    let startTime = this.getStartTimeForPerformanceEvent(event);
    if (!startTime) {
      return undefined;
    }

    let endEventName = scenarioQosCompletedEvent.error === undefined ? event.successName : event.failureName;
    this.performance.mark(endEventName);

    try {
      this.performance.measure(event.name, event.startedName, endEventName);
      let measurements: PerformanceEntry[] = this.performance.getEntriesByName(event.name, "measure");
      if (measurements.length == 0) {
        this.completePerfQos(scenarioQosCompletedEvent, "unable to find performance measure", false);
        return;
      }
      scenarioQosCompletedEvent.durationMs = Math.floor(measurements[0].duration);
      scenarioQosCompletedEvent.wallClockDurationMs = scenarioQosCompletedEvent.durationMs;
    } finally {
      this.performance.clearMarks(event.startedName);
      this.performance.clearMarks(endEventName);
      this.performance.clearMeasures(event.name);
    }
    return scenarioQosCompletedEvent;
  }

  private completePerfQos(
    scenarioQosCompletedEvent: ScenarioQosCompletedEvent,
    error: string,
    wasComplete: boolean
  ): ScenarioQosCompletedEvent {
    scenarioQosCompletedEvent.error = error;
    scenarioQosCompletedEvent.incomplete = !wasComplete;
    scenarioQosCompletedEvent.success = error === undefined;
    return scenarioQosCompletedEvent;
  }

  private createStartedEvent(event: PerformanceEventImpl, data?: CustomKeyValues): ScenarioQosStartedEvent {
    return {
      event: {
        name: event.name,
        options: event.options,
        timesRecorded: event.timesRecorded
      },
      data: data,
      scenarioQosStartedEventVersion: performanceQosEventsVersion
    };
  }

  private createCheckpointEvent(event: PerformanceEventImpl, data?: CustomKeyValues): ScenarioQosCheckpointEvent {
    return {
      event: {
        name: event.name,
        options: event.options,
        timesRecorded: event.timesRecorded
      },
      data: data,
      performanceQosCheckpointEventVersion: performanceQosEventsVersion
    };
  }

  private createCompletedEvent(
    event: PerformanceEventImpl,
    error?: string,
    data?: CustomKeyValues
  ): ScenarioQosCompletedEvent {
    return {
      event: {
        name: event.name,
        options: event.options,
        timesRecorded: event.timesRecorded
      },
      error: error,
      data: data,
      scenarioQosCompletedEventVersion: performanceQosEventsVersion
    };
  }

  public recordNavigateAway() {
    let navigateAwayTimeMs = new Date().getTime();
    this.crossSessionEvents.forEach((event) => {
      let startTime = <number>this.getStartedTimeForCrossSessionEvent(event, false);
      if (
        event.options.isCrossWindowOrTab &&
        startTime &&
        !this.$window.localStorage.getItem(event.startDurationLocalStorageKey)
      ) {
        let startDuration = navigateAwayTimeMs - startTime;
        let initialDurationEntry: InitialDurationEntry = {
          duration: startDuration,
          sessionIdentifer: this.sessionIdentifer
        };
        this.$window.localStorage.setItem(event.startDurationLocalStorageKey, JSON.stringify(initialDurationEntry));
      }
    });
  }
}
